import React from 'react';
import { Box, Chip, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, alpha } from '@mui/material';
import type { Grade } from '../../../types';
import type { StudentReportData } from '../../../services/analyticsService';
import { getGradeColorFromPercentage } from '../../../utils/gradeUtils';
import { SectionCard } from '../../common/SectionCard';
import EmptyState from '../../common/EmptyState';
import { Insights } from '@mui/icons-material';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A score as a short bar in its grade's colour, then the figure. */
export const ScoreBar: React.FC<{ pct: number | null; grades: Grade[] }> = ({ pct, grades }) => (pct == null
  ? <Typography variant="body2" color="text.disabled">—</Typography>
  : (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, pct))}
        sx={{
          width: 64, height: 6, borderRadius: 3, bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: getGradeColorFromPercentage(pct, grades) },
        }}
      />
      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{pct.toFixed(1)}%</Typography>
    </Stack>
  ));

const head = { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.4, py: 1 } as const;

const Chips: React.FC<{ label: string; names: string[]; color: 'success' | 'warning' }> = ({ label, names, color }) => (names.length === 0 ? null : (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5 }}>{label}</Typography>
    {names.map(n => (
      <Chip
        key={n}
        label={n}
        size="small"
        sx={t => ({ height: 22, fontWeight: 600, bgcolor: alpha(t.palette[color].main, 0.1), color: t.palette[color].dark })}
      />
    ))}
  </Stack>
));

/** How the student did: every exam's score, every subject's average, best and lowest, and where they are strong or need work. */
const AcademicCard: React.FC<{ report: StudentReportData; grades: Grade[] }> = ({ report, grades }) => {
  const perf = report.academicData.academicPerformance;
  // The server lists exams latest first — as the PDF does.
  const exams = report.academicData.resultsHistory as Array<{ examId: string; examName: string; month: number; year: number; overallPercentage: number | null; subjects: unknown[] }>;
  const subjects = perf.subjectWisePerformance;

  if (exams.length === 0 && subjects.length === 0) {
    return (
      <SectionCard title="Academic performance">
        <EmptyState dense icon={<Insights />} title="No results yet" description="Exam results for this session appear here once marks are entered." />
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Academic performance">
      {exams.length > 0 && (
        <Box sx={{ mx: -2.5, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...head, pl: 2.5 }}>Exam</TableCell>
                <TableCell sx={head}>When</TableCell>
                <TableCell sx={head}>Score</TableCell>
                <TableCell sx={{ ...head, pr: 2.5 }} align="right">Papers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exams.map(e => (
                <TableRow key={e.examId} hover>
                  <TableCell sx={{ pl: 2.5 }}><Typography variant="body2" fontWeight={600}>{e.examName}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary" noWrap>{MONTHS[e.month - 1]} {e.year}</Typography></TableCell>
                  <TableCell><ScoreBar pct={e.overallPercentage} grades={grades} /></TableCell>
                  <TableCell sx={{ pr: 2.5 }} align="right"><Typography variant="body2" color="text.secondary">{e.subjects.length}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {subjects.length > 0 && (
        <Box sx={{ mx: -2.5, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...head, pl: 2.5 }}>Subject</TableCell>
                <TableCell sx={head}>Average</TableCell>
                <TableCell sx={head} align="right">Best</TableCell>
                <TableCell sx={head} align="right">Lowest</TableCell>
                <TableCell sx={{ ...head, pr: 2.5 }} align="right">Tests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.map(s => (
                <TableRow key={s.subjectName} hover>
                  <TableCell sx={{ pl: 2.5 }}><Typography variant="body2" fontWeight={600}>{s.subjectName}</Typography></TableCell>
                  <TableCell><ScoreBar pct={s.averagePercentage} grades={grades} /></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600} color="success.main">{s.bestPerformance.toFixed(1)}%</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600} color="error.main">{s.worstPerformance.toFixed(1)}%</Typography></TableCell>
                  <TableCell sx={{ pr: 2.5 }} align="right"><Typography variant="body2" color="text.secondary">{s.totalTests}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Stack spacing={1}>
        <Chips label="Strong areas" names={perf.strengths} color="success" />
        <Chips label="Needs work" names={perf.improvements} color="warning" />
      </Stack>
    </SectionCard>
  );
};

export default AcademicCard;
